import React, { useState } from 'react';
import { LiquidAlertProps } from './LiquidAlert.types';
import { useAlgUITheme } from '../../context/algUIThemeContext';
import { getThemeConfig } from '../../utils/themes';

const LiquidAlert: React.FC<LiquidAlertProps> = ({
  children,
  type = 'info',
  title,
  showIcon = true,
  dismissible = false,
  onDismiss,
  size = 'md',
  variant = 'clean',
  className = '',
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const themeContext = useAlgUITheme();
  const globalTheme = themeContext?.theme || 'crystal-light';
  const effectiveTheme = globalTheme === 'system' ? themeContext?.systemTheme || 'crystal-light' : globalTheme;
  const themeColorsConfig = getThemeConfig(effectiveTheme as 'crystal-light' | 'plasma-dark');

  const sizeClasses = {
    sm: 'p-3 text-sm',
    md: 'p-4 text-base',
    lg: 'p-6 text-lg'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const typeConfig = {
    info: {
      icon: (
        <svg className={iconSizeClasses[size]} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
      color: themeColorsConfig.colors.accentInfo,
      bgColor: 'rgba(59, 130, 246, 0.1)',
      borderColor: 'rgba(59, 130, 246, 0.3)'
    },
    success: {
      icon: (
        <svg className={iconSizeClasses[size]} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      color: themeColorsConfig.colors.accentSuccess,
      bgColor: 'rgba(16, 185, 129, 0.1)',
      borderColor: 'rgba(16, 185, 129, 0.3)'
    },
    warning: {
      icon: (
        <svg className={iconSizeClasses[size]} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      color: themeColorsConfig.colors.accentWarning,
      bgColor: 'rgba(245, 158, 11, 0.1)',
      borderColor: 'rgba(245, 158, 11, 0.3)'
    },
    error: {
      icon: (
        <svg className={iconSizeClasses[size]} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      color: themeColorsConfig.colors.accentError,
      bgColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: 'rgba(239, 68, 68, 0.3)'
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  const config = typeConfig[type];

  const alertClasses = `
    relative rounded-lg border
    ${sizeClasses[size]}
    transition-all duration-200 ease-out
    ${className}
  `.trim();

  return (
    <div
      className={alertClasses}
      style={{
        background: `linear-gradient(135deg, 
          ${themeColorsConfig.colors.glassBackground} 0%, 
          ${themeColorsConfig.colors.backgroundSecondary} 100%)`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${config.borderColor}`,
        boxShadow: `
          inset 0 1px 3px rgba(255, 255, 255, 0.1),
          0 4px 6px rgba(0, 0, 0, 0.1),
          0 1px 3px rgba(0, 0, 0, 0.08)
        `,
        color: themeColorsConfig.colors.textPrimary
      }}
      role="alert"
      {...props}
    >
      <div className="flex items-start">
        {showIcon && (
          <div 
            className="flex-shrink-0 mr-3 mt-0.5"
            style={{ color: config.color }}
          >
            {config.icon}
          </div>
        )}
        
        <div className="flex-1">
          {title && (
            <h3 className="font-semibold mb-1" style={{ color: config.color }}>
              {title}
            </h3>
          )}
          <div className="text-sm">
            {children}
          </div>
        </div>
        
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 ml-3 p-1 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Dismiss alert"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default LiquidAlert; 